import React, { useCallback, useEffect, useRef, useState } from 'react';
import maplibregl from 'maplibre-gl';
import { LineFollower, type FollowingState } from './LineFollower';
import { type VehicleManager } from '../../managers/VehicleManager';
import { VehicleCustomLayer } from './VehicleCustomLayer';
import { AnimationManager } from '../../managers/AnimationManager';
import 'maplibre-gl/dist/maplibre-gl.css';
import { TripService } from '../../services/TripService';
import TPGcolors from '../../config/TPGcolors.json';
import VehiclePopup, { type PopupContent } from './VehiclePopup';
import { useLocationContext } from '../../store/LocationContext';
// @ts-expect-error pas de librairie de type
import NoSleepApp from 'no-sleep-app';
import navGuide from '../../image/icons/NavGuide.png';
import userPoint from '../../image/icons/UserPoint.png';
import { useEphemeral } from '../../store/EphemeralContext';
import { AnimationDurationCalculator } from './AnimationDurationCalculator';
import { NavigationPointAnimator } from './NavigationPointAnimator';

const noSleep = new NoSleepApp();

const coloursMap = new Map<string, string>(TPGcolors.map((c) => [c.ref, c.colour]));

interface MapComponentProps {
  vehicleManager: VehicleManager;
  showVehicle: boolean;
}

const tripService = new TripService();
const DEBOUNCE_DELAY = 500; // en millisecondes

export const MapComponent: React.FC<MapComponentProps> = ({ vehicleManager, showVehicle }) => {
  const mapContainer = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const [animationManager, setAnimationManager] = useState<AnimationManager>();
  const debounceTimeout = useRef<number | null>(null);
  const loading = useRef(false);
  const vehicleLayer = useRef<VehicleCustomLayer>();
  const [popupData, setPopupData] = useState({
    visible: false,
    lngLat: [0, 0] as [number, number],
    content: undefined as PopupContent | undefined
  });

  const {
    state: { trackingMode, activeRouteGeometry },
    setMap
  } = useEphemeral();
  const { userLocation } = useLocationContext();

  // Référence au LineFollower
  const lineFollowerRef = useRef<LineFollower | null>(null);

  // État pour le suivi de ligne
  const [followingState, setFollowingState] = useState<FollowingState | null>(null);

  // Compteur des mises à jour pour débogage
  const updateCountRef = useRef(0);

  // Identifiants des lignes détectées pour débogage
  const [detectedLineId, setDetectedLineId] = useState<string | null>(null);

  const trackingModeRef = useRef(trackingMode);

  // Ajouter une référence au calculateur de durée d'animation
  const durationCalculator = useRef<AnimationDurationCalculator>(new AnimationDurationCalculator());

  // Référence à l'animateur de point de navigation
  const navigationAnimator = useRef<NavigationPointAnimator | null>(null);

  // Gestionnaire d'état de suivi
  const handleFollowingStateChange = useCallback(
    (state: FollowingState) => {
      setFollowingState(state);
      if (state.lineId !== detectedLineId) {
        setDetectedLineId(state.lineId);
      }
      updateCountRef.current += 1;
    },
    [detectedLineId]
  );

  // Mettre à jour le LineFollower lorsque l'itinéraire actif change
  useEffect(() => {
    const lineFollower = lineFollowerRef.current;
    if (lineFollower !== null && activeRouteGeometry !== null && activeRouteGeometry !== undefined) {
      lineFollower.setActiveRouteGeometry(activeRouteGeometry);
    }
  }, [activeRouteGeometry]);

  // (Re)mise à zéro quand on sort du mode tracking
  useEffect(() => {
    trackingModeRef.current = trackingMode;
    if (!trackingMode) {
      if (mapRef.current != null && !mapRef.current.isEasing()) {
        mapRef.current.easeTo({
          pitch: 0,
          bearing: 0,
          duration: 500
        });
      }

      // Réinitialiser le LineFollower
      if (lineFollowerRef.current != null) {
        lineFollowerRef.current.reset();
      }

      // Arrêter l'animation du point de navigation
      if (navigationAnimator.current != null) {
        navigationAnimator.current.stop();
      }

      // Cacher la couche de navigation animée
      if (mapRef.current != null) {
        const map = mapRef.current;
        if (map.getLayer('navigation-guide-animated-layer') != null) {
          map.setLayoutProperty('navigation-guide-animated-layer', 'visibility', 'none');
        }
      }

      noSleep.disable();
    } else {
      noSleep.enable();

      // Initialiser le LineFollower si ce n'est pas déjà fait
      if (mapRef.current != null && lineFollowerRef.current == null) {
        lineFollowerRef.current = new LineFollower(mapRef.current, {
          detectionRadius: 50, // 50 mètres
          maxOffLineDistance: 30, // 30 mètres
          stickToLineThreshold: 15 // 15 mètres
        });

        lineFollowerRef.current.setActiveRouteGeometry(activeRouteGeometry);
        // S'abonner aux changements d'état
        lineFollowerRef.current.onStateChange(handleFollowingStateChange);
      }
    }
  }, [activeRouteGeometry, handleFollowingStateChange, trackingMode]);

  // Gère l'affichage du popup véhicule
  const handleUpdatePopup = useCallback((visible: boolean, lngLat: [number, number], content?: PopupContent) => {
    setPopupData({ visible, lngLat, content });
  }, []);

  // Chargement des véhicules
  const loadVehiclesInView = useCallback(async () => {
    if (mapRef.current == null || !showVehicle) return;
    if (debounceTimeout.current !== null) {
      window.clearTimeout(debounceTimeout.current);
    }
    debounceTimeout.current = window.setTimeout(() => {
      if (loading.current) return;
      loading.current = true;
      void (async () => {
        try {
          if (mapRef.current == null) return;
          const currentMap = mapRef.current;
          const bounds = currentMap.getBounds();
          const southWest = bounds.getSouthWest();
          const northEast = bounds.getNorthEast();

          // Calculer les nouvelles coordonnées pour une zone deux fois plus grande
          const deltaLng = Math.abs(northEast.lng - southWest.lng);
          const deltaLat = Math.abs(northEast.lat - southWest.lat);

          const newMin = {
            lng: southWest.lng - deltaLng / 2,
            lat: southWest.lat - deltaLat / 2
          };

          const newMax = {
            lng: northEast.lng + deltaLng / 2,
            lat: northEast.lat + deltaLat / 2
          };

          const shapeData = (
            await tripService.getAll({
              min: newMin,
              max: newMax,
              minTime: Date.now() - 60 * 1000,
              maxTime: Date.now() + 60 * 1000,
              myTime: Date.now()
            })
          ).data;
          if (Array.isArray(shapeData)) {
            vehicleManager.loadVehicles(
              shapeData
                .filter((s: any) => s.route_short_name !== '92')
                .map((s: any) => ({
                  date: parseInt(s.date),
                  lat: parseFloat(s.lat),
                  lon: parseFloat(s.lon),
                  route_color: s.route_color === '' ? coloursMap.get(s.route_short_name)?.substring(1) : s.route_color,
                  route_short_name: s.route_short_name,
                  route_type: s.route_type,
                  tripId: s.tripId,
                  outBearing: parseFloat(s.outBearing),
                  inBearing: parseFloat(s.inBearing),
                  inSpeed: s.inSpeed === 'Infinity' ? 0.1 : parseFloat(s.inSpeed),
                  outSpeed: s.outSpeed === 'Infinity' ? 0.1 : parseFloat(s.outSpeed),
                  isStop: s.isStop === '1',
                  shape_pt_sequence: parseInt(s.shape_pt_sequence),
                  stop_sequence: parseInt(s.stop_sequence)
                }))
            );
            if (vehicleLayer.current !== undefined) {
              vehicleLayer.current.refreshData();
            }
          }
        } catch (e) {
          console.error(e);
        } finally {
          loading.current = false;
        }
      })();
    }, DEBOUNCE_DELAY);
  }, [showVehicle, vehicleManager]);

  useEffect(() => {
    if (showVehicle) {
      void loadVehiclesInView();
      animationManager?.start();
    } else {
      animationManager?.stop();
    }
  }, [animationManager, loadVehiclesInView, showVehicle]);

  useEffect(() => {
    const inter = setInterval(() => {
      if (showVehicle) {
        void loadVehiclesInView();
      }
    }, 50000);
    return () => {
      clearInterval(inter);
    };
  }, [loadVehiclesInView, showVehicle]);

  // Mise à jour avec les nouvelles positions GPS
  useEffect(() => {
    if (!trackingMode || lineFollowerRef.current == null) return;

    // Vérifier la validité des coordonnées
    if (typeof userLocation.latitude !== 'number' || typeof userLocation.longitude !== 'number') {
      return;
    }

    // Mettre à jour le LineFollower avec la nouvelle position
    lineFollowerRef.current.updatePosition({
      latitude: userLocation.latitude,
      longitude: userLocation.longitude,
      accuracy: userLocation.accuracy ?? 10,
      heading: userLocation.heading,
      speed: userLocation.speed,
      timestamp: Date.now()
    });
  }, [trackingMode, userLocation.latitude, userLocation.longitude, userLocation.heading, userLocation.speed, userLocation.accuracy]);

  // Mise à jour de la carte en fonction de l'état de suivi
  useEffect(() => {
    if (!trackingMode || mapRef.current == null || followingState == null) return;

    const map = mapRef.current;

    // Mise à jour de la source "user-location" sur la carte
    const userSource = map.getSource('user-location') as maplibregl.GeoJSONSource;
    if (userSource !== undefined) {
      userSource.setData({
        type: 'FeatureCollection',
        features: [
          {
            type: 'Feature',
            geometry: {
              type: 'Point',
              coordinates: followingState.onLine ? followingState.matchedPosition : followingState.rawPosition
            },
            properties: {
              bearing: followingState.heading
            }
          }
        ]
      });
    }

    // Mise à jour de la source "detected-line" si disponible
    if (followingState.onLine && lineFollowerRef.current != null) {
      const lineGeometry = lineFollowerRef.current.getCurrentLineGeometry();
      if (lineGeometry != null) {
        const lineSource = map.getSource('detected-line') as maplibregl.GeoJSONSource;
        if (lineSource !== undefined) {
          lineSource.setData({
            type: 'Feature',
            properties: {},
            geometry: lineGeometry
          });

          // Rendre la ligne visible
          map.setLayoutProperty('detected-line-layer', 'visibility', 'visible');
        }
      }
    } else {
      // Cacher la ligne si on n'en suit pas
      map.setLayoutProperty('detected-line-layer', 'visibility', 'none');
    }
    // Calcul de la durée d'animation
    const speedFactor = Math.min(1, Math.max(0.3, followingState.speed / 10));

    // Calculer la durée d'animation basée sur la fréquence des mises à jour GPS
    const duration = durationCalculator.current.updateWithNewTimestamp(Date.now());

    // Position à utiliser (projetée sur la ligne ou brute)
    const targetPosition = followingState.onLine ? followingState.matchedPosition : followingState.rawPosition;

    // Animer le marqueur de navigation
    if (navigationAnimator.current != null) {
      // Utiliser la direction de la ligne pour l'animation du marqueur
      navigationAnimator.current.animateTo(targetPosition, followingState.heading, duration);
    }

    // Calcul de l'inclinaison
    const basePitch = 45;
    const maxPitch = 60;
    const pitch = basePitch + speedFactor * (maxPitch - basePitch);
    const container = map.getContainer();
    const offsetY = container.clientHeight * 0.3; // 30% de la hauteur ; ajustez selon le rendu désiré

    // Animation fluide de la caméra
    map.easeTo({
      center: followingState.onLine ? followingState.matchedPosition : followingState.rawPosition,
      pitch,
      // Utiliser exclusivement la direction de la ligne (stockée dans heading par LineFollower)
      // Cette valeur est maintenant toujours la direction de la ligne, jamais le GPS ou la boussole
      bearing: followingState.heading,
      duration,
      easing: (t) => {
        // Easing personnalisé pour une animation plus fluide
        return t;
      },
      offset: [0, offsetY] as [number, number]
    });

    if (map.getLayer('user-location-layer') != null) {
      map.setLayoutProperty('user-location-layer', 'visibility', trackingMode ? 'none' : 'visible');
      if (!trackingMode) {
        map.moveLayer('user-location-layer');
      }
    }
    // if (map.getLayer('navigation-guide-layer') != null) {
    //   map.setLayoutProperty('navigation-guide-layer', 'visibility', trackingMode ? 'visible' : 'none');
    //   if (trackingMode) {
    //     map.moveLayer('navigation-guide-layer');
    //   }
    // }
    if (map.getLayer('navigation-guide-animated-layer') != null) {
      map.setLayoutProperty('navigation-guide-animated-layer', 'visibility', 'visible');
      if (trackingMode) {
        map.moveLayer('navigation-guide-animated-layer');
      }
    }
  }, [trackingMode, followingState]);

  // Mise à jour quand on n'est PAS en mode tracking
  useEffect(() => {
    if (trackingMode || mapRef.current == null) return;

    const map = mapRef.current;

    // Mise à jour de la position brute
    const userSource = map.getSource('user-location') as maplibregl.GeoJSONSource;
    if (userSource !== undefined) {
      userSource.setData({
        type: 'FeatureCollection',
        features: [
          {
            type: 'Feature',
            geometry: {
              type: 'Point',
              coordinates: [userLocation.longitude ?? 0, userLocation.latitude ?? 0]
            },
            properties: { bearing: 0 }
          }
        ]
      });
    }

    // Ajuster les visibilités
    if (map.getLayer('navigation-guide-layer') != null) {
      map.setLayoutProperty('navigation-guide-layer', 'visibility', 'none');
    }

    if (map.getLayer('user-location-layer') != null) {
      map.setLayoutProperty('user-location-layer', 'visibility', 'visible');
    }

    if (map.getLayer('detected-line-layer') != null) {
      map.setLayoutProperty('detected-line-layer', 'visibility', 'none');
    }
  }, [trackingMode, userLocation.latitude, userLocation.longitude]);

  // --- Initialisation de la carte ---
  useEffect(() => {
    if (mapContainer.current == null) return;
    if (mapRef.current !== null) return;

    const intervalHandler = setInterval(() => {
      // Patiente que le container prenne les dimensions correctes
      if (mapContainer.current === null || Math.abs(mapContainer.current.offsetWidth - window.innerWidth) > 5) return;
      clearInterval(intervalHandler);

      const map = new maplibregl.Map({
        container: mapContainer.current,
        maxZoom: 18,
        style: 'https://api.maptiler.com/maps/openstreetmap/style.json?key=VL8sEvEOFIaN4AiGM6xC',
        center: [userLocation.longitude ?? 6.1388916, userLocation.latitude ?? 46.2109031],
        zoom: 15,
        // @ts-expect-error librairie de type incomplète
        antialias: true,
        doubleClickZoom: false,
        attributionControl: {
          compact: true,
          customAttribution: _APP_VERSION_
        }
      });

      mapRef.current = map;
      vehicleManager.setMap(map);
      setMap(map);

      if (showVehicle) {
        void loadVehiclesInView();
      }

      navigationAnimator.current = new NavigationPointAnimator((position, bearing) => {
        // Fonction de callback pour mettre à jour la source GeoJSON
        const map = mapRef.current;
        if (map == null) return;

        const navSource = map.getSource('navigation-animated') as maplibregl.GeoJSONSource;
        if (navSource !== undefined) {
          navSource.setData({
            type: 'FeatureCollection',
            features: [
              {
                type: 'Feature',
                geometry: {
                  type: 'Point',
                  coordinates: position
                },
                properties: {
                  bearing
                }
              }
            ]
          });
        }
      });

      map.on('load', () => {
        // Ajout du layer véhicules
        vehicleLayer.current = new VehicleCustomLayer(vehicleManager, handleUpdatePopup);
        map.addLayer(vehicleLayer.current as any);

        const animManager = new AnimationManager(vehicleManager, vehicleLayer.current);
        animManager.start();
        setAnimationManager(animManager);

        // Source pour le marqueur user
        map.addSource('user-location', {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: [
              {
                type: 'Feature',
                geometry: {
                  type: 'Point',
                  coordinates: [userLocation.longitude ?? 0, userLocation.latitude ?? 0]
                },
                properties: {}
              }
            ]
          }
        });

        // Source pour la ligne détectée
        map.addSource('detected-line', {
          type: 'geojson',
          data: {
            type: 'Feature',
            properties: {},
            geometry: {
              type: 'LineString',
              coordinates: []
            }
          }
        });

        // Couche pour la ligne détectée
        map.addLayer({
          id: 'detected-line-layer',
          type: 'line',
          source: 'detected-line',
          layout: {
            'line-join': 'round',
            'line-cap': 'round',
            visibility: 'none'
          },
          paint: {
            'line-color': '#4882c5',
            'line-width': 6,
            'line-opacity': 0.8,
            // Bordure pour mieux voir la ligne détectée
            'line-gap-width': 1
          }
        });

        // Couche pour le pointeur utilisateur standard
        map.addLayer({
          id: 'user-location-layer',
          type: 'symbol',
          source: 'user-location',
          layout: {
            'icon-image': 'user-marker-icon',
            'icon-size': 0.15,
            'icon-allow-overlap': true,
            'icon-ignore-placement': true,
            'symbol-z-order': 'viewport-y'
          }
        });

        // Couche pour le pointeur de navigation
        map.addLayer({
          id: 'navigation-guide-layer',
          type: 'symbol',
          source: 'user-location',
          layout: {
            'icon-image': 'navigation-guide',
            'icon-size': 0.35,
            'icon-allow-overlap': true,
            'icon-rotate': ['get', 'bearing'],
            'icon-rotation-alignment': 'map',
            visibility: 'none'
          },
          paint: {
            'icon-opacity': 0.9
          }
        });

        // Chargement icône marqueur
        void map
          .loadImage(userPoint)
          .then((image) => {
            if (!map.hasImage('user-marker-icon')) {
              map.addImage('user-marker-icon', image.data);
            }
          })
          .catch((error) => {
            throw error;
          });

        // Source pour le marqueur de navigation animé
        map.addSource('navigation-animated', {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: [
              {
                type: 'Feature',
                geometry: {
                  type: 'Point',
                  coordinates: [userLocation.longitude ?? 0, userLocation.latitude ?? 0]
                },
                properties: { bearing: 0 }
              }
            ]
          }
        });

        // Couche pour le pointeur de navigation animé
        map.addLayer({
          id: 'navigation-guide-animated-layer',
          type: 'symbol',
          source: 'navigation-animated',
          layout: {
            'icon-image': 'navigation-guide',
            'icon-size': 0.35,
            'icon-allow-overlap': true,
            'icon-rotate': ['get', 'bearing'],
            'icon-rotation-alignment': 'map',
            visibility: 'none'
          },
          paint: {
            'icon-opacity': 0.9
          }
        });

        // Masquer l'ancienne couche de navigation-guide
        map.setLayoutProperty('navigation-guide-layer', 'visibility', 'none');

        // Chargement icône marqueur navigation
        void map
          .loadImage(navGuide)
          .then((image) => {
            if (!map.hasImage('navigation-guide')) {
              map.addImage('navigation-guide', image.data);
            }
          })
          .catch((error) => {
            throw error;
          });

        // Initialiser le LineFollower si en mode tracking
        if (trackingMode && lineFollowerRef.current == null) {
          lineFollowerRef.current = new LineFollower(map, {
            detectionRadius: 50, // 50 mètres
            maxOffLineDistance: 30, // 30 mètres
            stickToLineThreshold: 15 // 15 mètres
          });

          // S'abonner aux changements d'état
          lineFollowerRef.current.onStateChange(handleFollowingStateChange);
        }

        return () => {
          animManager.stop();
          if (vehicleLayer.current !== undefined && map.getLayer(vehicleLayer.current.id) !== undefined) {
            map.removeLayer(vehicleLayer.current.id);
            vehicleLayer.current = undefined;
          }

          // Nettoyer les callbacks du LineFollower
          if (lineFollowerRef.current != null) {
            lineFollowerRef.current.offStateChange(handleFollowingStateChange);
          }
        };
      });

      map.on('moveend', () => {
        if (!trackingModeRef.current) {
          debounceTimeout.current = window.setTimeout(() => {
            void loadVehiclesInView();
          }, DEBOUNCE_DELAY);
        }
      });
    }, 50);

    return () => {
      clearInterval(intervalHandler);
    };
  }, [
    handleUpdatePopup,
    loadVehiclesInView,
    setMap,
    showVehicle,
    trackingMode,
    userLocation.latitude,
    userLocation.longitude,
    vehicleManager,
    handleFollowingStateChange
  ]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void loadVehiclesInView();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [loadVehiclesInView, userLocation]);

  // Interface de débogage optionnelle
  const DebugOverlay = useCallback(() => {
    if (!trackingMode || followingState == null) return null;

    return (
      <div className="absolute bottom-4 left-4 right-4 bg-black bg-opacity-70 text-white p-2 rounded-lg text-xs">
        <div>
          GPS: {!Number.isNaN(userLocation.accuracy) ? userLocation.accuracy.toFixed(1) + 'm' : 'N/A'} | Updates: {updateCountRef.current}
        </div>
        <div>
          Speed: {(followingState.speed * 3.6).toFixed(1)} km/h | Heading: {followingState.heading.toFixed(0)}°
        </div>
        <div>
          Line: {followingState.onLine ? 'ON' : 'OFF'} | Conf: {(followingState.confidence * 100).toFixed(1)}%
        </div>
        <div>
          LineDir: {followingState.lineDirection?.toFixed(0)}° | {followingState.isReversed ? 'Inverse' : 'Normal'}
        </div>
        {followingState.lineId != null && <div>Line ID: {followingState.lineId}</div>}
      </div>
    );
  }, [trackingMode, followingState, userLocation.accuracy]);

  return (
    <div className="w-full h-full">
      <div ref={mapContainer} className="w-full h-full" />

      {/* Popups et overlays */}
      {mapRef.current !== undefined && <VehiclePopup visible={popupData.visible} lngLat={popupData.lngLat} map={mapRef.current} content={popupData.content} />}

      {/* Overlay de débogage */}
      {/* <DebugOverlay /> */}
    </div>
  );
};
